import React, {ErrorInfo, ReactNode} from 'react';

import ErrorComponent from '@/pages/(errors)/ErrorComponent';

/**
 * Type representing the state for the ErrorBoundary component
 */
type ErrorBoundaryStateT = {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
};

/**
 * Type representing the props for the ErrorBoundary component
 */
type ErrorBoundaryPropsT = {
  children: ReactNode;
};

/**
 * ErrorBoundary component provides a way to catch JavaScript errors
 * that occur anywhere in the child component tree and display a fallback
 * UI instead of crashing the entire application
 *
 * @extends Component
 */
class ErrorBoundary extends React.Component<ErrorBoundaryPropsT, ErrorBoundaryStateT> {
  constructor(props: ErrorBoundaryPropsT) {
    super(props);

    // Define a state variable to track whether there is an error or not
    this.state = {hasError: false};
  }

  /**
   * A static lifecycle method called when an error is caught
   * Updates the state so the next render will show the fallback UI
   *
   * @returns {ErrorBoundaryStateT} The updated state with hasError set to true
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryStateT {
    // Update state so the next render will show the fallback UI
    return {hasError: true, error};
  }

  /**
   * A lifecycle method called after an error has been thrown by a descendant component
   * Useful for logging error information to an external service
   *
   * @param {Error} error - The error that was thrown
   * @param {ErrorInfo} errorInfo - An object with information about the error
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Check if this is an auth error
    if (error.message.includes('AuthProvider')) {
      // Let auth errors bubble up to a higher error boundary
      throw error;
    }

    // For other errors, handle them here
    console.error({error, errorInfo});
    this.setState({error, errorInfo});
  }

  /**
   * Reset error state when props change
   */
  componentDidUpdate(prevProps: ErrorBoundaryPropsT) {
    if (prevProps !== this.props && this.state.hasError) {
      this.setState({hasError: false, error: undefined, errorInfo: undefined});
    }
  }

  /**
   * Renders the fallback UI if there is an error or the child components otherwise
   *
   * @returns {React.ReactNode} The rendered content
   */
  render(): React.ReactNode {
    // Check if the error is thrown
    if (this.state.hasError) {
      // Use our custom error component to display the error
      return (
        <ErrorComponent
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={() => this.setState({hasError: false, error: undefined, errorInfo: undefined})}
        />
      );
    }

    // No error thrown, return children components
    return this.props.children;
  }
}

export default ErrorBoundary;
